import store from "@/store/index.js";

/**
 * Righe prenotabili di un prodotto commerciale.
 *
 * L'unità prenotabile è la **varietà**, non il colore: due varietà distinte
 * possono avere lo stesso colore, e sapere che un certo rosa è Yahoo anziché
 * Zoe è l'informazione che conta. Il colore resta come identificativo visivo
 * e come raggruppamento.
 *
 * Convivono due modelli:
 *  - varietà: `specie.variety`, ciascuna con il proprio `id_colore`
 *  - legacy: i colori embedded nel prodotto (`prodotto.colori`), usati dai
 *    prodotti senza specie e dallo storico scritto prima della migrazione
 *
 * Il discriminante è `prodotto.specie`. Le due sorgenti escono in una forma
 * unica, così i componenti non ramificano mai.
 *
 * Forma normalizzata:
 *   { id, tipo, nome, nome_colore, id_colore, id_serie, nome_serie,
 *     img, hex, esclusa, orfano }
 *   tipo: "varieta" | "colore" | "sconosciuto"
 *   id:   id varietà, oppure id colore nel legacy — è la chiave che finisce
 *         nel dato prenotato
 */

const API = process.env.VUE_APP_API_ROOT;

// id_colore -> documento colore (null = richiesto ma inesistente)
const cache_colori = new Map();
// id_colore -> Promise della richiesta che lo sta recuperando
const in_volo = new Map();

/* ------------------------------------------------------------------ */
/* Normalizzazione                                                     */
/* ------------------------------------------------------------------ */

function normalizza_varieta(varieta, specie, esclusa = false) {
  const colore = varieta.id_colore
    ? cache_colori.get(String(varieta.id_colore))
    : null;
  const serie =
    varieta.serie && specie && Array.isArray(specie.series)
      ? specie.series.find((s) => String(s.id) === String(varieta.serie))
      : null;
  return {
    id: String(varieta.id),
    tipo: "varieta",
    nome: varieta.nome || "senza nome",
    nome_colore: colore ? colore.nome : null,
    id_colore: varieta.id_colore ? String(varieta.id_colore) : null,
    id_serie: varieta.serie ? String(varieta.serie) : null,
    nome_serie: serie ? serie.nome : null,
    img: varieta.id_colore
      ? API + "/specie/img_colore/" + varieta.id_colore
      : null,
    hex: colore && colore.hex ? colore.hex : null,
    // Il prodotto non produce questa varietà. Compare lo stesso quando ha
    // dei dati — una stagione passata in cui era prodotta — ma marcata.
    esclusa,
    orfano: false,
  };
}

function normalizza_legacy(colore, id_prodotto) {
  return {
    id: String(colore.id),
    tipo: "colore",
    nome: colore.nome,
    nome_colore: colore.nome,
    id_colore: String(colore.id),
    id_serie: null,
    nome_serie: null,
    img: API + "/prodotto/" + id_prodotto + "/img_colore/" + colore.id,
    hex: null,
    esclusa: false,
    orfano: false,
  };
}

// Riferimento presente nei dati ma non più risolvibile. Restituito invece di
// null perché la griglia deve poterlo mostrare (marcato) anziché far sparire
// una quantità esistente.
function voce_orfana(id) {
  return {
    id: String(id),
    tipo: "sconosciuto",
    nome: "Non trovata",
    nome_colore: null,
    id_colore: null,
    id_serie: null,
    nome_serie: null,
    img: null,
    hex: null,
    esclusa: false,
    orfano: true,
  };
}

/* ------------------------------------------------------------------ */
/* Cache dei colori referenziati dalle varietà                         */
/* ------------------------------------------------------------------ */

function assicura_in_cache(ids) {
  const mancanti = ids.filter((id) => !cache_colori.has(id) && !in_volo.has(id));

  if (mancanti.length) {
    const richiesta = store
      .dispatch("fetch_colori_by_id", mancanti)
      .then((res) => {
        (res || []).forEach((c) => cache_colori.set(String(c.id), c));
        // Il server filtra con id__in: gli id inesistenti non tornano.
        mancanti.forEach((id) => {
          if (!cache_colori.has(id)) cache_colori.set(id, null);
        });
      })
      .finally(() => {
        // Nessun catch: l'errore deve arrivare al componente. Non avendo
        // marcato nulla in cache, il retry rifà davvero la richiesta.
        mancanti.forEach((id) => in_volo.delete(id));
      });

    mancanti.forEach((id) => in_volo.set(id, richiesta));
  }

  const attese = ids.map((id) => in_volo.get(id)).filter(Boolean);
  return attese.length ? Promise.all(attese) : Promise.resolve();
}

function invalida_cache() {
  cache_colori.clear();
  in_volo.clear();
}

/* ------------------------------------------------------------------ */
/* Sorgenti                                                            */
/* ------------------------------------------------------------------ */

function specie_di(prodotto) {
  if (!prodotto || !prodotto.specie) return null;
  return store.getters.get_specie_by_id(prodotto.specie) || null;
}

function escluse_di(prodotto) {
  return new Set((prodotto && prodotto.varieta_escluse) || []);
}

function varieta_di(prodotto) {
  const specie = specie_di(prodotto);
  // Nessun filtro per serie: ogni prodotto di una specie può portare tutte
  // le varietà di quella specie.
  return specie && Array.isArray(specie.variety) ? specie.variety : [];
}

/* ------------------------------------------------------------------ */
/* API pubblica                                                        */
/* ------------------------------------------------------------------ */

/**
 * Voci di più prodotti, con una sola richiesta per i colori coinvolti.
 *
 * @returns {Promise<Map<string, Array>>} id_prodotto -> voci normalizzate
 */
async function carica_voci_prodotti(prodotti, opzioni = {}) {
  const lista = (prodotti || []).filter(Boolean);

  const da_recuperare = new Set();
  lista.forEach((prodotto) => {
    varieta_di(prodotto).forEach((v) => {
      if (v.id_colore) da_recuperare.add(String(v.id_colore));
    });
  });
  if (da_recuperare.size) {
    await assicura_in_cache([...da_recuperare]);
  }

  const risultato = new Map();

  lista.forEach((prodotto) => {
    const id_prodotto = String(prodotto.id);
    const varieta = varieta_di(prodotto);

    // Senza specie, o con una specie priva di varietà, resta il legacy.
    if (!varieta.length) {
      risultato.set(
        id_prodotto,
        (prodotto.colori || []).map((c) => normalizza_legacy(c, id_prodotto))
      );
      return;
    }

    const specie = specie_di(prodotto);
    const escluse = escluse_di(prodotto);
    let voci = varieta.map((v) =>
      normalizza_varieta(v, specie, escluse.has(String(v.id)))
    );

    // Le escluse spariscono da qualunque elenco: non si prenota e non si
    // produce una varietà che quel vaso non fa. Quelle con dati rientrano
    // comunque da unisci_voci_presenti, marcate.
    if (!opzioni.includi_escluse) {
      voci = voci.filter((v) => !v.esclusa);
    }

    if (opzioni.solo_disponibili) {
      const disponibili = prodotto.colori_disponibili || [];
      voci = voci.filter((v) => disponibili.includes(v.id_colore));
    }

    // Serie, poi colore, poi nome: la serie è la discriminante che conta,
    // e dentro una serie il confronto si fa fra varietà dello stesso colore.
    voci.sort((a, b) => {
      const serie = (a.nome_serie || "").localeCompare(b.nome_serie || "");
      if (serie !== 0) return serie;
      const colore = (a.nome_colore || "").localeCompare(b.nome_colore || "");
      return colore !== 0 ? colore : (a.nome || "").localeCompare(b.nome || "");
    });

    risultato.set(id_prodotto, voci);
  });

  return risultato;
}

/** Comodità per un prodotto solo. */
async function carica_voci(prodotto, opzioni = {}) {
  const mappa = await carica_voci_prodotti([prodotto], opzioni);
  return mappa.get(String(prodotto.id)) || [];
}

/**
 * Accoda alle voci della sorgente quelle che compaiono nei dati ma che la
 * sorgente non elenca: è il caso dello storico scritto per colore prima che
 * il prodotto passasse alle varietà. Restano riconoscibili da `tipo`.
 */
function unisci_voci_presenti(prodotto, voci, ids_presenti) {
  const note = new Set(voci.map((v) => v.id));
  const extra = [...new Set((ids_presenti || []).map(String))]
    .filter((id) => !note.has(id))
    .map((id) => trova_voce(prodotto, id));
  return extra.length ? voci.concat(extra) : voci;
}

/**
 * Risolve un singolo id già presente nei dati, cercandolo fra le varietà
 * della specie e poi fra i colori embedded del prodotto.
 *
 * Sincrona: presuppone la cache calda per i nomi colore, quindi va chiamata
 * dopo carica_voci_prodotti. Un id irrisolvibile restituisce un segnaposto
 * con `orfano: true` anziché null.
 */
function trova_voce(prodotto, id_voce) {
  const id = String(id_voce);

  const varieta = varieta_di(prodotto).find((v) => String(v.id) === id);
  if (varieta) {
    return normalizza_varieta(
      varieta,
      specie_di(prodotto),
      escluse_di(prodotto).has(id)
    );
  }

  const legacy = (prodotto && prodotto.colori ? prodotto.colori : []).find(
    (c) => String(c.id) === id
  );
  if (legacy) return normalizza_legacy(legacy, String(prodotto.id));

  return voce_orfana(id);
}

export default {
  carica_voci,
  carica_voci_prodotti,
  unisci_voci_presenti,
  trova_voce,
  invalida_cache,
};
